import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { useToast } from '../components/Toaster.tsx';

type SkillLevel = 'prime' | 'second' | 'learn';

interface AgentLite {
  id: string;
  name: string;
  email: string;
  role: 'agent' | 'admin' | 'time_admin';
  isFullTime: boolean;
  phoneNumber: string | null;
}

interface SkillLite {
  id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

interface MatrixCell {
  agentId: string;
  skillId: string;
  level: SkillLevel;
}

interface MatrixPayload {
  agents: AgentLite[];
  skills: SkillLite[];
  cells: MatrixCell[];
}

// Skill-centric directory. Each skill is one row with three cells:
//   Prime  | Second | Learning
// Each cell lists the agents at that level for that skill, name-linked
// to their profile (/agents/:id). Optimized for "who's best at X?" —
// scan the Prime column. To answer "what does X do?", click their name
// and read the buckets on the profile page.
//
// This page is read-only. Skill editing lives on /agents/:id (your own
// profile, or any agent's if you're an admin) — keeps the directory
// uncluttered and consolidates the edit surface.

export function TeamDirectoryPage(): React.ReactElement {
  useDocumentTitle('Team directory');
  const { agent: me } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<MatrixPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [busy, setBusy] = useState(false);

  function reload() {
    apiFetch<MatrixPayload>('/api/agents/matrix')
      .then(setData)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Failed to load.'));
  }

  useEffect(() => {
    reload();
  }, []);

  async function addSkill(): Promise<void> {
    const name = newSkillName.trim();
    if (!name) return;
    // New skills go to the bottom by giving them a display_order past the
    // current max. The admin can shuffle them later via the catalog UI
    // (or directly in SQL — there's no UI for reorder yet).
    const maxOrder = data?.skills.reduce((m, s) => Math.max(m, s.displayOrder), 0) ?? 0;
    setBusy(true);
    try {
      await apiFetch('/api/skills', {
        method: 'POST',
        json: { name, displayOrder: maxOrder + 10 },
      });
      toast.success(`Added "${name}".`);
      setNewSkillName('');
      setAdding(false);
      reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to add skill.');
    } finally {
      setBusy(false);
    }
  }

  // skill_id → { prime: agents[], second: agents[], learn: agents[] }
  const bySkill = useMemo(() => {
    if (!data) return new Map<string, Record<SkillLevel, AgentLite[]>>();
    const agentById = new Map(data.agents.map((a) => [a.id, a]));
    const map = new Map<string, Record<SkillLevel, AgentLite[]>>();
    for (const s of data.skills) {
      map.set(s.id, { prime: [], second: [], learn: [] });
    }
    for (const c of data.cells) {
      const agent = agentById.get(c.agentId);
      const bucket = map.get(c.skillId);
      if (!agent || !bucket) continue;
      bucket[c.level].push(agent);
    }
    for (const b of map.values()) {
      for (const lvl of Object.keys(b) as SkillLevel[]) {
        b[lvl].sort((x, y) => x.name.localeCompare(y.name));
      }
    }
    return map;
  }, [data]);

  const filteredSkills = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.skills;
    return data.skills.filter((s) => s.name.toLowerCase().includes(q));
  }, [data, query]);

  if (error) return <Card><p className="text-sm text-[var(--color-danger)]">{error}</p></Card>;
  if (!data) return <p className="text-[var(--color-muted)]">Loading…</p>;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">Team directory</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Who's best at what. Each skill lists the techs at Prime (their primary), Second (capable
          backup), and Learning (actively skilling up). Click a name to see their hours and contact
          info. To change a rating, open your own profile (or any tech's, if you're an admin).
        </p>
      </header>

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            placeholder="Filter skills (e.g. Azure, Network, Quotes…)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 min-w-[260px] flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
          />
          <span className="text-xs text-[var(--color-muted)]">
            {filteredSkills.length} of {data.skills.length} skills · {data.agents.length} active techs
          </span>
          {me?.role === 'admin' && !adding && (
            <Button variant="outline" onClick={() => setAdding(true)}>
              + Add skill
            </Button>
          )}
        </div>
        {me?.role === 'admin' && adding && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-[var(--radius-button)] border border-[var(--color-t3-blue)]/30 bg-[var(--color-t3-blue)]/5 p-3">
            <input
              type="text"
              autoFocus
              placeholder="New skill name (e.g. Intune, Fortinet, SQL Server…)"
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addSkill();
                else if (e.key === 'Escape') {
                  setAdding(false);
                  setNewSkillName('');
                }
              }}
              maxLength={100}
              className="h-9 min-w-[280px] flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 text-sm"
            />
            <Button onClick={() => void addSkill()} disabled={busy || !newSkillName.trim()}>
              {busy ? 'Adding…' : 'Add'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setAdding(false);
                setNewSkillName('');
              }}
            >
              Cancel
            </Button>
            <p className="basis-full text-xs text-[var(--color-muted)]">
              The new row appears at the bottom. Tick agent ratings via each tech's profile page.
            </p>
          </div>
        )}
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <th className="w-[280px] px-4 py-3 font-semibold">Skill</th>
                <th className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <Dot kind="prime" /> Prime
                  </span>
                  <span className="ml-1.5 font-normal normal-case text-[var(--color-muted)]">— best at it</span>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <Dot kind="second" /> Second
                  </span>
                  <span className="ml-1.5 font-normal normal-case text-[var(--color-muted)]">— capable backup</span>
                </th>
                <th className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <Dot kind="learn" /> Learning
                  </span>
                  <span className="ml-1.5 font-normal normal-case text-[var(--color-muted)]">— skilling up</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSkills.map((s, i) => {
                const bucket = bySkill.get(s.id) ?? { prime: [], second: [], learn: [] };
                return (
                  <tr
                    key={s.id}
                    className={
                      'border-t border-[var(--color-border)] align-top ' +
                      (i % 2 === 0 ? 'bg-white' : 'bg-[var(--color-surface)]/30')
                    }
                  >
                    <td className="px-4 py-3 font-semibold text-[var(--color-header)]">
                      {s.name}
                    </td>
                    <LevelCell level="prime" agents={bucket.prime} />
                    <LevelCell level="second" agents={bucket.second} />
                    <LevelCell level="learn" agents={bucket.learn} />
                  </tr>
                );
              })}
              {filteredSkills.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-[var(--color-muted)]">
                    No skills match that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function LevelCell({
  level,
  agents,
}: {
  level: SkillLevel;
  agents: AgentLite[];
}): React.ReactElement {
  if (agents.length === 0) {
    return <td className="px-4 py-3 text-sm italic text-[var(--color-muted)]">—</td>;
  }
  return (
    <td className="px-4 py-3">
      <ul className="flex flex-wrap gap-1.5">
        {agents.map((a) => (
          <li key={a.id}>
            <Link
              to={`/agents/${a.id}`}
              className={
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ' +
                (level === 'prime'
                  ? 'bg-[var(--color-t3-blue)] text-white hover:brightness-95'
                  : level === 'second'
                    ? 'bg-[var(--color-t3-purple)] text-white hover:brightness-95'
                    : 'bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200')
              }
              title={`Open ${a.name}'s profile`}
            >
              {a.name}
              {!a.isFullTime && (
                <span
                  className={
                    'rounded-full px-1 text-[9px] font-semibold uppercase tracking-wide ' +
                    (level === 'learn'
                      ? 'bg-amber-200 text-amber-900'
                      : 'bg-white/30 text-white')
                  }
                  title="Part-time"
                >
                  PT
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </td>
  );
}

function Dot({ kind }: { kind: SkillLevel }): React.ReactElement {
  const cls =
    kind === 'prime'
      ? 'bg-[var(--color-t3-blue)]'
      : kind === 'second'
        ? 'bg-[var(--color-t3-purple)]'
        : 'bg-amber-400';
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />;
}
